import { Container, Graphics, Sprite, Texture, RenderTexture } from '../../../vendor/pixi.min.mjs';

/**
 * PlatformerLighting — a lightweight 2D lightmap compositor.
 *
 * The look is the classic "multiply lightmap": the scene is rendered
 * normally, then a screen-space lightmap is multiplied over it.
 *
 *   final = scene × lightmap
 *
 * Where the lightmap is white the scene is untouched (fully lit); where it
 * is dark the scene is dimmed/tinted (ambient shadow); point lights paint
 * additive white glows that "cut holes" of brightness back into the dark.
 *
 * How it's built each frame (all in a small off-screen RenderTexture at a
 * fraction of the screen resolution — soft light doesn't need full res, so
 * this stays cheap enough for a Raspberry Pi / phone):
 *
 *   1. fill the lightmap with the AMBIENT colour scaled by ambient
 *      brightness (+ a uniform DIRECTIONAL wash so a "sun/moon" lifts the
 *      whole scene),
 *   2. add a faint DIRECTIONAL gradient so the lit side reads as the light's
 *      angle,
 *   3. additively stamp one soft radial sprite per POINT LIGHT at its
 *      projected screen position, tinted + scaled to its colour / radius /
 *      intensity.
 *
 * The result is rendered into the RenderTexture and shown through a single
 * full-screen Sprite with `blendMode = 'multiply'`, sitting above the world
 * container but below the HUD. The whole pass is one extra draw to a small
 * texture + one multiply sprite — negligible next to the scene itself, and
 * entirely skipped when a stage doesn't enable lighting (zero cost).
 *
 * The compositor owns no game logic: the scene hands it the list of
 * PointLightEntity instances and the live camera `{ camX, camY, zoom }`, and
 * it does the world→screen projection itself.
 */
export class PlatformerLighting {
    /**
     * @param {object} renderer  the Pixi renderer (game.app.renderer)
     * @param {number} viewW     canvas width  (px)
     * @param {number} viewH     canvas height (px)
     * @param {object} [opts]     { resScale } lightmap resolution factor
     */
    constructor(renderer, viewW, viewH, opts = {}) {
        this.renderer = renderer;
        this.viewW = Math.max(2, viewW | 0);
        this.viewH = Math.max(2, viewH | 0);
        // Render the lightmap at a fraction of screen res — light is low
        // frequency, so half res is visually free and ~4× cheaper.
        this.resScale = clamp01(opts.resScale ?? 0.5) || 0.5;

        // Stage config (filled by configure()).
        this.active = false;
        this._ambientColor = 0x202840;
        this._ambient = 0.35;
        this._dir = 0;
        this._dirColor = 0xfff2cc;
        this._dirAngle = 0;

        /** @type {Array<{sample:Function}>} point-light entities */
        this._lights = [];

        // Shared soft textures (generated once).
        this._radialTex = makeRadialTexture(256);
        this._linearTex = makeLinearTexture(256);

        // Off-screen lightmap stage.
        this._stage = new Container();
        this._base = new Graphics();
        this._stage.addChild(this._base);
        this._dirSprite = new Sprite(this._linearTex);
        this._dirSprite.anchor.set(0.5);
        this._dirSprite.blendMode = 'add';
        this._dirSprite.visible = false;
        this._stage.addChild(this._dirSprite);
        /** Reusable point-light glow sprites (object pool). */
        this._pool = [];

        this._rt = null;
        this.display = new Sprite();
        this.display.blendMode = 'multiply';
        this.display.visible = false;
        this._buildRT();
    }

    /** (Re)allocate the lightmap RenderTexture for the current view size. */
    _buildRT() {
        const w = Math.max(2, Math.round(this.viewW * this.resScale));
        const h = Math.max(2, Math.round(this.viewH * this.resScale));
        if (this._rt) this._rt.destroy(true);
        this._rt = RenderTexture.create({ width: w, height: h });
        this.display.texture = this._rt;
        // Scale the (low-res) lightmap back up to cover the whole screen.
        this.display.width = this.viewW;
        this.display.height = this.viewH;
        this.display.position.set(0, 0);
    }

    /**
     * Apply a stage's `lighting` block. Recognised keys:
     *   enabled      — master on/off (string 'on'/'off' or boolean)
     *   ambientColor — "#rrggbb" tint of the unlit shadow
     *   ambient      — 0..1 brightness of unlit areas (0 = pitch black)
     *   dir          — 0..1 directional ("sun") intensity (0 = off)
     *   dirColor     — "#rrggbb" sun/moon colour
     *   dirAngle     — 0..360° compass angle the light comes FROM
     */
    configure(lighting) {
        const L = lighting || {};
        const on = L.enabled === true || L.enabled === 'on';
        this.active = on;
        this._ambientColor = parseHex(L.ambientColor, 0x202840);
        this._ambient = clamp01(num(L.ambient, 0.35));
        this._dir = clamp01(num(L.dir, 0));
        this._dirColor = parseHex(L.dirColor, 0xfff2cc);
        this._dirAngle = num(L.dirAngle, 0);
        this.display.visible = this.active;
    }

    /** Hand the compositor the live point-light entity list. */
    setLights(list) { this._lights = Array.isArray(list) ? list : []; }

    /** React to a canvas resize — reallocate the lightmap. */
    resize(viewW, viewH) {
        this.viewW = Math.max(2, viewW | 0);
        this.viewH = Math.max(2, viewH | 0);
        this._buildRT();
    }

    /**
     * Rebuild + render the lightmap for this frame.
     * @param {number} dt   seconds since last frame (drives light flicker)
     * @param {{camX:number,camY:number,zoom:number}} cam  live camera
     */
    update(dt, cam) {
        if (!this.active) { this.display.visible = false; return; }
        this.display.visible = true;

        const rs = this.resScale;
        const lw = this.viewW * rs;
        const lh = this.viewH * rs;
        const z = (cam && cam.zoom) || 1;
        const camX = (cam && cam.camX) || 0;
        const camY = (cam && cam.camY) || 0;

        // 1) Ambient (+ uniform directional) floor. Baking a portion of the
        //    directional light into the flat fill makes the whole scene lift
        //    as the sun strengthens; the rest rides in the gradient below.
        let baseColor = scaleColor(this._ambientColor, this._ambient);
        if (this._dir > 0) {
            baseColor = addColor(baseColor, scaleColor(this._dirColor, this._dir * 0.55));
        }
        this._base.clear();
        this._base.rect(0, 0, lw, lh);
        this._base.fill({ color: baseColor, alpha: 1 });

        // 2) Directional gradient — a soft band brighter on the side the
        //    light comes from, so the angle reads. Compass angle (0 = top,
        //    clockwise) → screen vector, matching the particle convention.
        if (this._dir > 0) {
            const a = (this._dirAngle * Math.PI) / 180;
            const dx = Math.sin(a);
            const dy = -Math.cos(a);
            const diag = Math.hypot(lw, lh) * 1.15;
            this._dirSprite.visible = true;
            this._dirSprite.position.set(lw / 2, lh / 2);
            this._dirSprite.width = diag;
            this._dirSprite.height = diag;
            // Gradient is bright on its +X edge → aim +X toward the source.
            this._dirSprite.rotation = Math.atan2(dy, dx);
            this._dirSprite.tint = this._dirColor;
            this._dirSprite.alpha = this._dir * 0.45;
        } else {
            this._dirSprite.visible = false;
        }

        // 3) Point lights — one additive soft sprite each, projected to
        //    screen space and culled when fully off-view.
        let n = 0;
        for (const ent of this._lights) {
            if (!ent || ent.alive === false) continue;
            const L = ent.sample ? ent.sample(dt) : ent;
            if (!L || L.intensity <= 0) continue;
            const sx = (L.x - camX) * z * rs;
            const sy = (L.y - camY) * z * rs;
            const rPx = L.radius * z * rs;
            if (rPx <= 0) continue;
            if (sx + rPx < 0 || sx - rPx > lw || sy + rPx < 0 || sy - rPx > lh) continue;

            const spr = this._acquire(n++);
            spr.position.set(sx, sy);
            // Texture is 256px with the glow filling the full radius → scale
            // so the sprite's half-size equals the light radius.
            spr.width = rPx * 2;
            spr.height = rPx * 2;
            spr.tint = L.color;
            spr.alpha = Math.min(1, L.intensity);
        }
        // Hide any pooled sprites left over from a busier frame.
        for (let i = n; i < this._pool.length; i++) this._pool[i].visible = false;

        this.renderer.render({ container: this._stage, target: this._rt, clear: true });
    }

    /** Fetch (or lazily create) the pooled glow sprite at index `i`. */
    _acquire(i) {
        let spr = this._pool[i];
        if (!spr) {
            spr = new Sprite(this._radialTex);
            spr.anchor.set(0.5);
            spr.blendMode = 'add';
            this._stage.addChild(spr);
            this._pool[i] = spr;
        }
        spr.visible = true;
        return spr;
    }

    destroy() {
        this._lights = [];
        if (this.display.parent) this.display.parent.removeChild(this.display);
        this.display.destroy();
        this._stage.destroy({ children: true });
        if (this._rt) { this._rt.destroy(true); this._rt = null; }
        if (this._radialTex) { this._radialTex.destroy(true); this._radialTex = null; }
        if (this._linearTex) { this._linearTex.destroy(true); this._linearTex = null; }
        this._pool = [];
    }
}

// ── Texture factories ─────────────────────────────────────────────

/** White radial gradient (opaque centre → transparent edge), tinted per-use. */
function makeRadialTexture(size = 256) {
    const cvs = document.createElement('canvas');
    cvs.width = cvs.height = size;
    const ctx = cvs.getContext('2d');
    const r = size / 2;
    const g = ctx.createRadialGradient(r, r, 0, r, r, r);
    g.addColorStop(0.0, 'rgba(255,255,255,1)');
    g.addColorStop(0.45, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.8, 'rgba(255,255,255,0.12)');
    g.addColorStop(1.0, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    return Texture.from(cvs);
}

/** Horizontal gradient, transparent on -X, bright on +X (for directional). */
function makeLinearTexture(size = 256) {
    const cvs = document.createElement('canvas');
    cvs.width = size; cvs.height = 16;
    const ctx = cvs.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, size, 0);
    g.addColorStop(0.0, 'rgba(255,255,255,0)');
    g.addColorStop(1.0, 'rgba(255,255,255,1)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, 16);
    return Texture.from(cvs);
}

// ── Colour maths ──────────────────────────────────────────────────

const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);

function num(v, dflt) {
    const n = Number(v);
    return Number.isFinite(n) ? n : dflt;
}

function parseHex(v, dflt) {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
        const m = /^#?([0-9a-f]{6})$/i.exec(v.trim());
        if (m) return parseInt(m[1], 16);
    }
    return dflt;
}

/** Multiply each channel of a 0xRRGGBB colour by `k` (clamped 0..255). */
function scaleColor(hex, k) {
    const r = Math.min(255, Math.round(((hex >> 16) & 0xff) * k));
    const g = Math.min(255, Math.round(((hex >> 8) & 0xff) * k));
    const b = Math.min(255, Math.round((hex & 0xff) * k));
    return (r << 16) | (g << 8) | b;
}

/** Add two 0xRRGGBB colours per channel (clamped 0..255). */
function addColor(a, b) {
    const r = Math.min(255, ((a >> 16) & 0xff) + ((b >> 16) & 0xff));
    const g = Math.min(255, ((a >> 8) & 0xff) + ((b >> 8) & 0xff));
    const bl = Math.min(255, (a & 0xff) + (b & 0xff));
    return (r << 16) | (g << 8) | bl;
}
